import { useEffect, useState } from "react";
import { AppLayout } from "../components/Layout/AppLayout";
import { MapView } from "../components/MapView";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Download } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function MapaEnVivo() {
  const [positions, setPositions] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [coverageZones, setCoverageZones] = useState([]);
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [exportando, setExportando] = useState(null);

  const load = () => api.get("/field/locations").then((r) => setPositions(r.data)).catch(() => {});
  const loadZonas = () => api.get("/field/zonas-cobertura").then((r) => setCoverageZones(r.data)).catch(() => {});

  useEffect(() => {
    load();
    loadZonas();
    api.get("/gis/sectores").then((r) => setSectores(r.data)).catch(() => {});
    const id = setInterval(load, 180000);
    return () => clearInterval(id);
  }, []);

  const exportarRecorrido = async (userId, nombre) => {
    setExportando(userId);
    try {
      const { data } = await api.get(`/field/recorrido-pdf?user_id=${userId}&fecha=${fecha}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recorrido_${nombre.replace(/\s+/g, "_")}_${fecha}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Recorrido descargado");
    } catch (e) {
      toast.error("No se pudo generar el recorrido en PDF");
    } finally {
      setExportando(null);
    }
  };

  const conSenal = positions.filter((p) => p.lat && p.lon).length;

  return (
    <AppLayout title="Mapa en vivo — Personal de calle">
      <div className="mb-4 flex items-center gap-4 flex-wrap">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Encargados con señal</p>
            <p className="text-2xl font-black text-valoriza-leaf mt-1" data-testid="live-map-count">
              {conSenal} / {positions.length}
            </p>
          </CardContent>
        </Card>
        <div className="flex items-center gap-2">
          <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="w-40" data-testid="recorrido-fecha-input" />
          <p className="text-xs text-muted-foreground max-w-xs">Fecha para exportar el recorrido de cada encargado en PDF.</p>
        </div>
        {coverageZones.length > 0 && (
          <p className="text-xs text-accent-alert font-bold" data-testid="coverage-zones-note">
            {coverageZones.length} zona(s) de baja cobertura detectadas (naranja/rojo en el mapa)
          </p>
        )}
      </div>
      <MapView fieldPositions={positions} sectores={sectores} coverageZones={coverageZones} showHeatmap={false} showIncidents={false} showSectors={true} height="560px" />
      <div className="mt-4 space-y-2" data-testid="live-map-list">
        {positions.map((p) => (
          <div key={p.user_id} className="flex items-center justify-between rounded-md border border-border px-4 py-2 text-sm" data-testid={`live-map-row-${p.user_id}`}>
            <span className="font-medium">{p.nombre}</span>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">
                {p.actualizado_en ? `Actualizado: ${new Date(p.actualizado_en).toLocaleString("es-ES")}` : "Sin señal registrada todavía"}
              </span>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 h-7 text-xs"
                onClick={() => exportarRecorrido(p.user_id, p.nombre)}
                disabled={exportando === p.user_id}
                data-testid={`export-recorrido-${p.user_id}`}
              >
                <Download className="h-3 w-3" /> {exportando === p.user_id ? "Generando..." : "Recorrido PDF"}
              </Button>
            </div>
          </div>
        ))}
        {positions.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No hay encargados de campo activos.</p>}
      </div>
    </AppLayout>
  );
}
